// src/Components/BookAppointment.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";
import bgImage from "../Images/1.jpg";

const APPOINTMENT_API_URL = "https://adgrohairgloskinkanchipuram.in/api/email.php";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    "& fieldset": { borderColor: "white" },
    "&:hover fieldset": { borderColor: "white" },
    "&.Mui-focused fieldset": { borderColor: "white" },
  },
  "& .MuiInputLabel-root": {
    color: "white",
    "&.Mui-focused": { color: "white" },
  },
  "& .MuiInputBase-input": { color: "white" },
  "& svg": { color: "white" },
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#1a1a1a",
      "& .MuiMenuItem-root": {
        color: "white",
        "&:hover": { bgcolor: "rgba(249,57,73,0.2)" },
        "&.Mui-selected": { bgcolor: "rgba(249,57,73,0.3)" },
        "&.Mui-selected:hover": { bgcolor: "rgba(249,57,73,0.4)" },
      },
    },
  },
};

const BookAppointment = () => {
  const [date, setDate] = useState(dayjs());
  const [timeSlot, setTimeSlot] = useState("");
  const [treatment, setTreatment] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",   // FIXED name to match EmailJS
    time: new Date().toLocaleString(),
  });

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const formRef = useRef();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOk = () => {
    setOpen(false);
    navigate("/thankyou");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(APPOINTMENT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          phone: formData.phone,
          date: date ? date.format("DD/MM/YYYY") : "",
          time: timeSlot,
          treatment,
          source: "Book Appointment - Website",
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Submission failed");
      }

      setOpen(true);

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        time: new Date().toLocaleString(),
      });
      setTimeSlot("");
      setTreatment("");
      setDate(dayjs());
    } catch (error) {
      console.error("FAILED...", error);
      alert("Failed to book appointment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: { lg: "100vh", sm: "60vh" },
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 4,
        py: 5,
        width: { lg: "100%", sm: "92%", md: "95%" },
      }}
    >
      <Box
        sx={{
          width: { lg: "45%", xs: "60%", sm: "70%" },
          bgcolor: "rgba(0,0,0,0.75)",
          p: 6,
          borderRadius: 3,
          mt: { sm: 8, xs: 10, md: 2, lg: 10 },
        }}
        data-aos="fade-up"
      >
        <Typography
          fontSize={{ xs: "1.5rem" }}
          align="center"
          fontWeight="bold"
          color="#ffffff"
          mb={4}
          data-aos="zoom-in"
        >
          Book Your Appointment
        </Typography>

        <Box component="form" ref={formRef} onSubmit={handleSubmit}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2}>

              {/* FIRST NAME */}
              <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
                <TextField
                  fullWidth
                  name="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  variant="outlined"
                  sx={inputStyles}
                  required
                />
              </Grid>

              {/* LAST NAME */}
              <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
                <TextField
                  fullWidth
                  name="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  variant="outlined"
                  sx={inputStyles}
                  required
                />
              </Grid>

              {/* EMAIL */}
              <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
                <TextField
                  fullWidth
                  type="email"
                  name="email"
                  label="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  sx={inputStyles}
                  required
                />
              </Grid>

              {/* PHONE - FIXED */}
              <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
                <TextField
                  fullWidth
                  type="tel"
                  name="phone"   // FIXED name
                  label="Your Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  sx={inputStyles}
                  required
                />
              </Grid>

              {/* DATE FIXED */}
              <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
                <DatePicker
                  value={date}
                  onChange={(newDate) => setDate(newDate)}
                  minDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      InputLabelProps: {
                        shrink: true,
                        sx: { color: "white", "&.Mui-focused": { color: "white" } },
                      },
                      InputProps: {
                        sx: {
                          color: "white",
                          "& input": { color: "white" },
                          "& .MuiSvgIcon-root": { color: "white" },
                        },
                      },
                      sx: inputStyles,
                    },
                  }}
                />
              </Grid>

              {/* TIME SLOT FIXED */}
              <Grid size={{ xs: 12, sm: 6, lg: 6 }}>
                <TextField
                  select
                  fullWidth
                  name="timeSlot"
                  value={timeSlot}
                  onChange={(e) => setTimeSlot(e.target.value)}
                  variant="outlined"
                  sx={inputStyles}
                  SelectProps={{ displayEmpty: true, MenuProps: selectMenuProps }}
                  required
                >
                  <MenuItem value="" disabled>Select time slot</MenuItem>
                  {[
                    "10.00 AM to 11:00 AM",
                    "11.00 AM to 12:00 PM",
                    "12.00 PM to 1:00 PM",
                    "1.00 PM to 2:00 PM",
                    "2.00 PM to 3:00 PM",
                    "3.00 PM to 4:00 PM",
                    "4.00 PM to 5:00 PM",
                    "5.00 PM to 6:00 PM",
                    "6.00 PM to 7:00 PM",
                    "7.00 PM to 8:00 PM",
                  ].map((slot) => (
                    <MenuItem key={slot} value={slot}>{slot}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* TREATMENT - FIXED name */}
              <Grid size={{ xs: 12, sm: 12, lg: 12 }}>
                <TextField
                  select
                  fullWidth
                  name="treatment"
                  value={treatment}
                  onChange={(e) => setTreatment(e.target.value)}
                  variant="outlined"
                  sx={inputStyles}
                  SelectProps={{ displayEmpty: true, MenuProps: selectMenuProps }}
                  required
                >
                  <MenuItem value="" disabled>Select Treatment</MenuItem>
                  <MenuItem value="Facial">Facial</MenuItem>
                  <MenuItem value="Hair Treatment">Hair Treatment</MenuItem>
                  <MenuItem value="Skin Peel">Skin Peel</MenuItem>
                </TextField>
              </Grid>

              {/* HIDDEN DATE FIELD */}
              <input
                type="hidden"
                name="date"
                value={date ? date.format("DD/MM/YYYY") : ""}
              />

              {/* HIDDEN TIME FIELD */}
              <input type="hidden" name="time" value={formData.time} />

              {/* BUTTON */}
              <Grid
                size={{ xs: 12, sm: 12, lg: 12 }}
                display="flex"
                justifyContent="center"
                mt={2}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{
                    bgcolor: "#f93949",
                    px: 5,
                    py: 1.5,
                    fontWeight: "bold",
                    fontSize: { lg: "16px", xs: "10px" },
                    borderRadius: "8px",
                    "&:hover": { bgcolor: "#d72b3b" },
                    "&.Mui-disabled": { bgcolor: "#f9394988", color: "#fff" },
                  }}
                >
                  {submitting ? "Booking..." : "Book Appointment"}
                </Button>

                <Dialog open={open} onClose={handleOk}>
                  <DialogTitle>Booking Confirmed ✅</DialogTitle>
                  <DialogContent>
                    <Typography>
                      Your appointment has been booked successfully!
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleOk}
                      sx={{ mt: 2 }}
                    >
                      OK
                    </Button>
                  </DialogContent>
                </Dialog>
              </Grid>

            </Grid>
          </LocalizationProvider>
        </Box>
      </Box>
    </Box>
  );
};

export default BookAppointment;
